/**
 * Best Friend Club — recommendation engine.
 *
 * Turns a customer's questionnaire answers into a feeding plan: grams a day, bags per delivery, how often
 * a delivery comes, and what each one costs. Pure calculation: no network, no storage, no Shopify.
 *
 * Every number comes from the config (config/engine-config.json), never from this file, so BFC can change
 * prices, profile factors, breeds and bag rules without touching code. The rules are BFC's
 * Final Calculation & Subscription Inputs of 26 August 2026; test/golden.test.js holds BFC's ten worked
 * profiles as the acceptance test.
 *
 * One file, no dependencies, no build step. The same file runs in the browser (the storefront questionnaire
 * loads it as a theme asset and reads `window.BFCEngine`) and in Node (`require('./engine.js')`), so the
 * price a customer sees and the price the server charges come from identical code.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.BFCEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var LB_TO_KG = 0.45359237;
  var DAY_MS = 86400000;

  function fail(message) { throw new Error('BFC engine: ' + message); }
  function isNum(n) { return typeof n === 'number' && isFinite(n); }

  /** Round half up to `decimals` places. toFixed(8) first removes float noise (1.005 * 100 = 100.49999…). */
  function roundHalfUp(n, decimals) {
    var f = Math.pow(10, decimals);
    return Math.round(Number((n * f).toFixed(8))) / f;
  }

  /** Pounds to whole pence, half up. */
  function toPence(pounds) { return Math.round(Number((pounds * 100).toFixed(8))); }

  /* ---------------------------------------------------------------- config validation */

  function validateConfig(c) {
    if (!c || typeof c !== 'object') fail('config is missing');

    if (!c.recipes || !Object.keys(c.recipes).length) fail('config needs at least one recipe');
    Object.keys(c.recipes).forEach(function (k) {
      if (!(c.recipes[k].kcalPerGram > 0)) fail('recipe "' + k + '" needs a kcalPerGram above 0');
    });

    ['yes', 'no', 'unsure'].forEach(function (n) {
      ['couch_potato', 'typical', 'active'].forEach(function (a) {
        ['lose', 'about_right', 'gain'].forEach(function (g) {
          var v = c.profileFactors && c.profileFactors[n] && c.profileFactors[n][a] && c.profileFactors[n][a][g];
          if (!(isNum(v) && v > 0)) fail('profile factor missing or invalid for ' + [n, a, g].join(' / '));
        });
      });
    });

    if (!c.breeds || !Object.keys(c.breeds).length) fail('config needs the breed table');
    Object.keys(c.breeds).forEach(function (name) {
      if (!isNum(c.breedModifiers[c.breeds[name]])) fail('breed "' + name + '" has unknown category "' + c.breeds[name] + '"');
    });
    if (!isNum(c.unknownBreedModifier)) fail('unknownBreedModifier must be a number');
    if (!(c.maxBreedsInMix >= 1)) fail('maxBreedsInMix must be 1 or more');

    if (!(c.daysPerMonth > 0)) fail('daysPerMonth must be above 0');

    var b = c.bags || {};
    if (!Array.isArray(b.sizesKg) || !b.sizesKg.length) fail('bags.sizesKg must list the bag sizes');
    if (!(b.incrementKg > 0)) fail('bags.incrementKg must be above 0');
    if (!(b.maxParcelKg >= Math.max.apply(null, b.sizesKg))) fail('bags.maxParcelKg must fit the largest bag');

    var p = c.pricing || {};
    if (!Array.isArray(p.anchors) || p.anchors.length < 2) fail('pricing needs at least two anchors');
    p.anchors.forEach(function (a, i) {
      if (!Array.isArray(a) || !isNum(a[0]) || !isNum(a[1]) || a[1] <= 0) fail('pricing anchor ' + (i + 1) + ' must be [monthlyKg, pricePerKg]');
      if (i && a[0] <= p.anchors[i - 1][0]) fail('pricing anchors must be in ascending monthly kg');
    });
    if (!(p.holdRateBelow > 0) || !(p.holdRateAbove > 0)) fail('pricing hold rates must be above 0');
    if (!(p.rateLookupDecimals >= 0)) fail('pricing.rateLookupDecimals must be 0 or more');
    if (!(p.attributionDiscountPercent >= 0 && p.attributionDiscountPercent < 100)) fail('attributionDiscountPercent must be 0 to 99');

    if (!(c.eligibility && c.eligibility.adultMinMonths >= 0 && c.eligibility.maxWeightKg > 0)) fail('eligibility rules are missing');
  }

  /* ---------------------------------------------------------------- the engine */

  function createEngine(config) {
    validateConfig(config);
    var c = config;
    var anchors = c.pricing.anchors;
    var sizes = c.bags.sizesKg.slice().sort(function (x, y) { return y - x; });   /* largest first */

    /** Price per kg for a monthly quantity: linear between anchors, held flat outside them. */
    function ratePerKg(monthlyKg) {
      if (monthlyKg <= anchors[0][0]) return c.pricing.holdRateBelow;
      if (monthlyKg >= anchors[anchors.length - 1][0]) return c.pricing.holdRateAbove;
      for (var i = 0; i < anchors.length - 1; i++) {
        var lo = anchors[i], hi = anchors[i + 1];
        if (monthlyKg <= hi[0]) return lo[1] + (monthlyKg - lo[0]) * (hi[1] - lo[1]) / (hi[0] - lo[0]);
      }
      return c.pricing.holdRateAbove;
    }

    /** Nearest whole-bag quantity in increments, exact midpoints up, never less than one increment. */
    function allocateKg(monthlyKg) {
      var inc = c.bags.incrementKg;
      return Math.max(Math.floor(Number((monthlyKg / inc).toFixed(8)) + 0.5) * inc, inc);
    }

    /** Fewest bags: largest size first. Returns { 10: n, 2: n }. */
    function bagsFor(kg) {
      var out = {}, left = kg;
      sizes.forEach(function (s) { out[s] = Math.floor(Number((left / s).toFixed(8))); left -= out[s] * s; });
      if (Math.abs(left) > 1e-9) fail(kg + 'kg cannot be made from whole bags');
      return out;
    }

    /** Pack the cycle's bags into parcels of at most maxParcelKg, largest bags first. */
    function parcelsFor(kg) {
      var bags = bagsFor(kg), parcels = [], current = null;
      sizes.forEach(function (s) {
        for (var i = 0; i < bags[s]; i++) {
          if (!current || current.kg + s > c.bags.maxParcelKg) {
            current = { kg: 0, bags: emptyBags() };
            parcels.push(current);
          }
          current.kg += s;
          current.bags[s]++;
        }
      });
      return parcels;
    }
    function emptyBags() { var o = {}; sizes.forEach(function (s) { o[s] = 0; }); return o; }

    /** Mean of the selected breeds' modifiers; no breed selected means unknown. */
    function breedModifier(names) {
      names = names || [];
      if (!names.length) return c.unknownBreedModifier;
      if (names.length > c.maxBreedsInMix) fail('a mix can have at most ' + c.maxBreedsInMix + ' breeds');
      var sum = 0;
      names.forEach(function (n) {
        if (!Object.prototype.hasOwnProperty.call(c.breeds, n)) fail('"' + n + '" is not in the breed table');
        sum += c.breedModifiers[c.breeds[n]];
      });
      return sum / names.length;
    }

    function profileFactor(neutered, activity, goal) {
      var byN = c.profileFactors[neutered];
      if (!byN) fail('neutered must be yes, no or unsure');
      var byA = byN[activity];
      if (!byA) fail('activity must be couch_potato, typical or active');
      var f = byA[goal];
      if (!isNum(f)) fail('goal must be lose, about_right or gain');
      return f;
    }

    function weightInKg(input) {
      var kg = isNum(input.weightKg) ? input.weightKg
        : (isNum(input.weight) ? (input.weightUnit === 'lb' ? input.weight * LB_TO_KG : input.weight) : NaN);
      if (!(kg > 0) || kg > c.eligibility.maxWeightKg) fail('weight must be above 0 and at most ' + c.eligibility.maxWeightKg + 'kg');
      return kg;
    }

    /**
     * The plan for one dog.
     * @param {{weightKg?:number, weight?:number, weightUnit?:'kg'|'lb', neutered:string, activity:string,
     *          goal:string, breeds:string[], recipe:string}} input
     */
    function plan(input) {
      var weightKg = weightInKg(input);
      var recipe = c.recipes[input.recipe];
      if (!recipe) fail('recipe must be one of ' + Object.keys(c.recipes).join(', '));

      var pf = profileFactor(input.neutered, input.activity, input.goal);
      var bm = breedModifier(input.breeds);
      var rer = 70 * Math.pow(weightKg, 0.75);
      var gramsPerDay = rer * pf * bm / recipe.kcalPerGram;
      var monthlyKg = gramsPerDay * c.daysPerMonth / 1000;
      var bagsKg = allocateKg(monthlyKg);
      var cycleDays = bagsKg * 1000 / gramsPerDay;

      /* BFC's worked profiles look the rate up on monthly kg as written (2 decimals), use that rate
         unrounded, and round only the final money. That reading reproduces all thirty of their figures. */
      var rate = ratePerKg(roundHalfUp(monthlyKg, c.pricing.rateLookupDecimals));
      var chargeExact = rate * bagsKg;
      var pence = toPence(chargeExact);

      return {
        engineVersion: c.version,
        recipe: input.recipe,
        weightKg: weightKg,
        profileFactor: pf,
        breedModifier: bm,
        rer: rer,
        gramsPerDay: gramsPerDay,
        monthlyKg: monthlyKg,
        bagsKg: bagsKg,
        bags: bagsFor(bagsKg),
        parcels: parcelsFor(bagsKg),
        cycleDays: cycleDays,
        cycleDaysDisplay: Math.round(cycleDays),
        ratePerKg: rate,
        cycleChargeExact: chargeExact,
        cycleChargePence: pence,
        perDayPence: toPence(chargeExact / cycleDays),
        perMonthPence: toPence(chargeExact / cycleDays * c.daysPerMonth),
      };
    }

    /** The cycle charge with a percentage off, applied to the unrounded list charge. */
    function discountedChargePence(p, percent) {
      var pct = isNum(percent) ? percent : c.pricing.attributionDiscountPercent;
      if (!(pct >= 0 && pct < 100)) fail('discount must be 0 to 99 percent');
      return { percent: pct, pence: toPence(p.cycleChargeExact * (100 - pct) / 100) };
    }

    /** Adult food only, and no veterinary diets. 'unsure' on the vet question may continue. */
    function eligibility(answers) {
      if (!(answers.ageMonths >= c.eligibility.adultMinMonths)) return { eligible: false, reason: 'puppy' };
      if (answers.vetDiet === 'yes') return { eligible: false, reason: 'vet_diet' };
      return { eligible: true, reason: null };
    }

    /**
     * The next `count` deliveries from `start`. Each cycle is charged once, on its first parcel. A cycle
     * over the parcel ceiling sends its later parcels on the day the food already sent runs out.
     * Days come from exact consumption and are rounded once per delivery, so rounding never accumulates.
     */
    function deliveries(p, opts) {
      opts = opts || {};
      var count = opts.count || 3;
      var start = opts.start ? new Date(opts.start.getTime()) : new Date();
      var out = [];
      for (var cycle = 0; out.length < count; cycle++) {
        var sentKg = 0;
        for (var i = 0; i < p.parcels.length && out.length < count; i++) {
          var exactDay = cycle * p.cycleDays + sentKg * 1000 / p.gramsPerDay;
          var dayOffset = Math.round(exactDay);
          out.push({
            cycle: cycle,
            dayOffset: dayOffset,
            date: new Date(start.getTime() + dayOffset * DAY_MS),
            kg: p.parcels[i].kg,
            bags: p.parcels[i].bags,
            charged: i === 0,
          });
          sentKg += p.parcels[i].kg;
        }
      }
      return out;
    }

    return {
      config: c,
      plan: plan,
      deliveries: deliveries,
      discountedChargePence: discountedChargePence,
      eligibility: eligibility,
      ratePerKg: ratePerKg,
      allocateKg: allocateKg,
      bagsFor: bagsFor,
      parcelsFor: parcelsFor,
      breedModifier: breedModifier,
      breedNames: function () { return Object.keys(c.breeds); },
      toPence: toPence,
    };
  }

  return { createEngine: createEngine, validateConfig: validateConfig, toPence: toPence };
});
